import styled from "@emotion/styled"
import { useAtomValue, useSetAtom } from "jotai"
import { ComponentProps, MutableRefObject, forwardRef, useEffect } from "react"
import usePlaybackUpdate from "../hooks/usePlaybackUpdate"
import { currentDurationAtom, isLoopEnabledAtom, isPlayingAtom } from "../state"

const Root = styled.video`
  width: 100%;
  height: 100%;
`

function Video(
  props: ComponentProps<typeof Root>,
  ref: MutableRefObject<HTMLVideoElement>
) {
  const setVideoPlaying = useSetAtom(isPlayingAtom)
  const setCurrentDuration = useSetAtom(currentDurationAtom)
  const isLoopEnabled = useAtomValue(isLoopEnabledAtom)
  const playbackUpdate = usePlaybackUpdate()

  useEffect(() => {
    setCurrentDuration(null)
  }, [setCurrentDuration])

  return (
    <Root
      crossOrigin="anonymous"
      {...props}
      ref={ref}
      loop={isLoopEnabled}
      controlsList="nodownload"
      onContextMenu={(e) => {
        e.preventDefault()
      }}
      onLoadedMetadata={(e) => {
        setCurrentDuration(e.currentTarget.duration)
        props.onLoadedMetadata?.(e)
      }}
      onTimeUpdate={(e) => {
        if (e.currentTarget.duration) {
          setCurrentDuration(e.currentTarget.duration)
        }
        playbackUpdate(e.currentTarget)
        props.onTimeUpdate?.(e)
      }}
      onEnded={(e) => {
        setVideoPlaying(false)
        props.onEnded?.(e)
      }}
      onPlay={(e) => {
        setVideoPlaying(true)
        props.onPlay?.(e)
      }}
      onPause={(e) => {
        const media = e.currentTarget
        // Browsers fire pause/play while seeking (often before `seeking` is
        // true). Defer so we don't flip isPlayingAtom on those transients —
        // otherwise the play button icon flickers play↔pause.
        window.setTimeout(() => {
          if (media.seeking || !media.paused) return
          setVideoPlaying(false)
          props.onPause?.(e)
        }, 0)
      }}
    />
  )
}

export default forwardRef(Video)
